import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {List} from '@Components/list';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import type {
  CommonDropDownPropsType,
  CommonListItemPropsType,
  ListAndListItems,
  OneOfPromptOption,
  PromptBoxConfigOptionValue,
  SelectedValueType,
} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {GENERATOR_OPTIONS_DROPDOWN} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {AiPromptBoxVariant, PromptBoxConfigOptionType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import usePromptConfigOptions from '@Hooks/ai-prompt-box/usePromptConfigOptions';
import {
  enumToInternationalizedString,
  getEnumFromOption,
  getListIdForConfigOption,
  getListItemIdForCofigOption,
  getOptionTypeFromListId,
  getOptionValueFromItemId,
  getRelevantIconClass,
  POPUP_OFFSET_ON_DESKTOP,
  POPUP_OFFSET_ON_MOBILE,
} from '@Libraries/ai-text-prompt-box-library';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {IconSize} from '@Components/icon-v2/icon.types';
import {useLeftRightScrollShadow} from '@Hooks/useLeftRightScrollShadow';
import {LIST_ITEM_TYPE} from '@/components/list/list.types';
import styles from './ai-text-prompt-box-generator-options.module.scss';

export interface AiTextPromptBoxGeneratorOptionsProps {
  id: string;
  darkerDropdown?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  isInMobileMode?: boolean;
  dropdownInUpdatedState?: PromptBoxConfigOptionType[];
  onOptionChange?: (changedProperty: OneOfPromptOption) => void;
  defaultValues?: SelectedValueType;
  optionsToUse?: PromptBoxConfigOptionType[];
  promptBoxVariant?: AiPromptBoxVariant;
  className?: string;
  closeOnSelect?: boolean;
}

const defaultOptionsToUse = [PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE, PromptBoxConfigOptionType.PURPOSE, PromptBoxConfigOptionType.EMOJIS];

enum PopupOffset {
  LEFT = 4,
  RIGHT = -4,
  NONE = 0,
}

/**
 * @author Moeed Ahmad <moeed@250mils.com>
 * */
export function AiTextPromptBoxGeneratorOptions({optionsToUse = defaultOptionsToUse, closeOnSelect = true, ...props}: AiTextPromptBoxGeneratorOptionsProps): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const currentlyOpenPopupId = useRef<string>('');
  const showScrollPills = props.promptBoxVariant === AiPromptBoxVariant.MINIATURE;
  const showShadowOverlay = props.promptBoxVariant === AiPromptBoxVariant.MINIATURE;
  const {leftShadow, rightShadow} = useLeftRightScrollShadow(containerRef, !!containerRef.current);
  const {selectedValues} = usePromptConfigOptions(optionsToUse, props.defaultValues, props.id);
  const [activeSelector, setActiveSelector] = useState<string | undefined>(undefined);

  useEffect(() => {
    const handleMakeSpace = (): void => {
      const elements = document.querySelectorAll('.-js-make-space-for-popup-when-open');
      if (elements.length > 0) {
        makeSpaceForPopup();
      } else {
        removeSpaceForPopup();
      }
    };

    handleMakeSpace();
    document.addEventListener('click', handleMakeSpace);

    const scrollContainer = containerRef.current;

    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', closePopupOnScroll, {passive: true});

    return (): void => {
      document.removeEventListener('click', handleMakeSpace);

      scrollContainer.removeEventListener('scroll', closePopupOnScroll);
    };
  }, [containerRef.current]);

  useEffect(() => {
    if (activeSelector !== undefined) {
      document.addEventListener('touchstart', closeConfigOptionList);
      document.addEventListener('mousedown', closeConfigOptionList);
    }

    return (): void => {
      document.removeEventListener('touchstart', closeConfigOptionList);
      document.removeEventListener('mousedown', closeConfigOptionList);
    };
  }, [activeSelector]);

  const closePopupOnScroll = (): void => {
    setActiveSelector(undefined);
  };

  const getPopupLeftOffset = (): number => {
    return PopupOffset.NONE;
  };

  const getPopupAlignment = (): ALIGNMENT_TYPE => {
    if (props.promptBoxVariant === AiPromptBoxVariant.MINIATURE) {
      return ALIGNMENT_TYPE.BOTTOM_START;
    }

    if (!props.isInMobileMode) {
      return ALIGNMENT_TYPE.BOTTOM_START;
    }

    return ALIGNMENT_TYPE.TOP_START;
  };

  const makeSpaceForPopup = (): void => {
    containerRef?.current?.classList.add(styles.makeSpaceForPopup);
  };

  const removeSpaceForPopup = (): void => {
    containerRef?.current?.classList.remove(styles.makeSpaceForPopup);
  };

  const toggleMakeSpaceForPopup = (id: string): void => {
    if (!containerRef?.current) {
      return;
    }

    if (currentlyOpenPopupId.current && currentlyOpenPopupId.current === id && containerRef.current.classList.contains(styles.makeSpaceForPopup)) {
      removeSpaceForPopup();
      return;
    }

    makeSpaceForPopup();
    currentlyOpenPopupId.current = id;
  };

  const getPopupTopOffset = (): number => {
    if (props.isInMobileMode && props.promptBoxVariant !== AiPromptBoxVariant.MINIATURE) {
      return POPUP_OFFSET_ON_MOBILE;
    }

    return POPUP_OFFSET_ON_DESKTOP;
  };

  const commonDropDownOptions: CommonDropDownPropsType = {
    popUpHasCustomWidth: true,
    className: `${styles.dropdown} ${GENERATOR_OPTIONS_DROPDOWN}`,
    popupSpacingClass: 'spacing-p-l-3 spacing-p-r-3 spacing-p-t-3 spacing-p-b-1',
    topOffset: getPopupTopOffset(),
    alignment: getPopupAlignment(),
  };

  const onSelectProperty = (listId: string, itemId: string): void => {
    const value = getOptionValueFromItemId(itemId);
    const type = getOptionTypeFromListId(listId);

    const changedProperty: OneOfPromptOption = {
      key: type,
      value,
    };

    if (props.onOptionChange) {
      props.onOptionChange(changedProperty);
    }

    if (closeOnSelect) {
      setActiveSelector(undefined);
    }
  };

  const commonListOptions: CommonListItemPropsType = {
    type: LIST_ITEM_TYPE.DEFAULT_8,
    onClick: onSelectProperty,
  };

  const listsAndItemsMap: ListAndListItems[] = optionsToUse.map((option) => {
    return {
      [option as string]: Object.values(getEnumFromOption(option)).map((optionValue: PromptBoxConfigOptionValue) => {
        return {
          text: enumToInternationalizedString(optionValue),
          id: getListItemIdForCofigOption(props.id, optionValue),
          isSelected: selectedValues[option] === optionValue,
          ...commonListOptions,
        };
      }),
    };
  });

  const isThisDropdownInUpdatedState = (type: PromptBoxConfigOptionType): boolean => {
    return props.dropdownInUpdatedState?.includes(type) ?? false;
  };

  const getSelectionText = (type: PromptBoxConfigOptionType): PromptBoxConfigOptionValue | null => {
    return selectedValues[type];
  };

  const getDropdownSelectorClassNames = (type: PromptBoxConfigOptionType): string => {
    return `
      ${styles.dropdownHeader} 
      ${props.darkerDropdown ? styles.inside : ''}
      ${props.isDisabled ? styles.disabled : ''} 
      ${props.promptBoxVariant === AiPromptBoxVariant.MINIATURE ? styles.lightPillDarkBorders : ''}
      ${isThisDropdownInUpdatedState(type) ? `${styles.dropdownHeaderActive} spacing-p-r-3` : ''}
    `;
  };

  const getSelector = (type: PromptBoxConfigOptionType): ReactElement => {
    return (
      <DropdownDefaultSelectBox
        icon={getRelevantIconClass(type)}
        id={`${props.id}-AiTextPromptBoxGeneratorOptions-dropdown-selectbox-${type}`}
        size={DROPDOWN_SIZE.XSMALL}
        text={enumToInternationalizedString(getSelectionText(type))}
        className={getDropdownSelectorClassNames(type)}
        openedClassName={`${styles.dropdownHeaderActive} ${props.darkerDropdown ? styles.inside : ''} -js-make-space-for-popup-when-open`}
        textClassName={`${styles.dropdownText} ${props.isDisabled ? styles.disabled : ''}`}
        iconClassName={`${styles.dropdownIcon} ${props.isDisabled ? styles.disabled : ''}`}
        isDisabled={props.isDisabled}
        isLoading={isThisDropdownInUpdatedState(type) && props.isLoading}
        isBold
        hideCaret
      />
    );
  };

  const scrollLeft = (): void => {
    if (!containerRef.current) {
      return;
    }

    containerRef.current.scrollLeft -= 100;
  };

  const scrollRight = (): void => {
    if (!containerRef.current) {
      return;
    }

    containerRef.current.scrollLeft += 100;
  };

  const createScrollPill = (customeClasses: string, icon: string, onClickAction: VoidFunction): ReactElement => {
    return (
      <Pill
        className={`${customeClasses} `}
        content={{
          type: PILL_CONTENT_TYPE.ICON,
          icon,
          size: IconSize.SIZE_ICON_20,
        }}
        size={PILL_SIZE.MEDIUM}
        onClick={onClickAction}
      />
    );
  };

  const getLeftScrollPill = (): ReactElement | null => {
    if (!showScrollPills || !leftShadow) {
      return null;
    }

    return createScrollPill(
      `${styles.scrollBtn} ${styles.leftBtn} ${getPopupAlignment() === ALIGNMENT_TYPE.TOP_START ? styles.aligmentTop : styles.alignmentBottom}`,
      'icon-caret-left',
      scrollLeft
    );
  };

  const getRightScrollPill = (): ReactElement | null => {
    if (!showScrollPills || !rightShadow) {
      return null;
    }

    return createScrollPill(
      `${styles.scrollBtn} ${styles.rightBtn} ${getPopupAlignment() === ALIGNMENT_TYPE.TOP_START ? styles.aligmentTop : styles.alignmentBottom}`,
      'icon-caret-right',
      scrollRight
    );
  };

  const getEdgesGradientOverlay = (customClasses: string): ReactElement | null => {
    return (
      <div className={`${styles.gradientOverlayContainer} ${customClasses} ${getPopupAlignment() === ALIGNMENT_TYPE.TOP_START ? styles.aligmentTop : styles.alignmentBottom}`} />
    );
  };

  const getRightOverlay = (): ReactElement | null => {
    return showShadowOverlay && rightShadow ? getEdgesGradientOverlay(styles.rightGradientOverlay) : null;
  };

  const getLeftOverlay = (): ReactElement | null => {
    return showShadowOverlay && leftShadow ? getEdgesGradientOverlay(styles.leftGradientOverlay) : null;
  };

  const handleOnClick = (id: string, optionType: PromptBoxConfigOptionType): void => {
    toggleMakeSpaceForPopup(id);

    if (optionType === activeSelector) {
      return;
    }
    setActiveSelector(optionType);
  };

  const closeConfigOptionList = (e: MouseEvent | TouchEvent): void => {
    const target = e.target as HTMLElement;

    const dropdownElements = document.querySelectorAll(`.${GENERATOR_OPTIONS_DROPDOWN}`);
    const clickedInside = Array.from(dropdownElements).some((el) => el.contains(target));

    if (!clickedInside) {
      setActiveSelector(undefined);
    }
  };

  const getOptionsSelector = (): ReactElement => {
    return (
      <div
        ref={containerRef}
        className={`flexbox spacing-p-r-2 ${styles.overflowXscrollableOnMobile} ${props.darkerDropdown ? styles.inside : ''} ${props.promptBoxVariant === AiPromptBoxVariant.MINIATURE ? styles.templateContentEditorClasses : ''} ${getPopupAlignment() === ALIGNMENT_TYPE.TOP_START ? styles.aligmentTop : styles.alignmentBottom}`}
      >
        {listsAndItemsMap.map((listObject) => {
          return Object.entries(listObject).map(([optionType, listItems]) => {
            return (
              <Dropdown
                key={`${props.id}-dropdown-${optionType}`}
                id={`${props.id}-dropdown-${optionType}`}
                popup={<List multiSelect={false} keepAtleastOneItemSelected id={getListIdForConfigOption(props.id, optionType as PromptBoxConfigOptionType)} items={listItems} />}
                popupClassName={`popupContainer ${styles.popup} ${optionType}`}
                selector={getSelector(optionType as PromptBoxConfigOptionType)}
                leftOffset={getPopupLeftOffset()}
                open={activeSelector === optionType}
                onClick={() => {
                  handleOnClick(`${props.id}-dropdown-${optionType}`, optionType as PromptBoxConfigOptionType);
                }}
                {...commonDropDownOptions}
              />
            );
          });
        })}
      </div>
    );
  };

  return (
    <div className={`${styles.generatorOptionsContainer} ${props.className ?? ''}`}>
      {getLeftOverlay()}
      {getLeftScrollPill()}
      {getOptionsSelector()}
      {getRightScrollPill()}
      {getRightOverlay()}
    </div>
  );
}
